import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/shop/Seo";

const BLOCKS = [
  {
    to: "/shop/selected-tackle",
    title: "SELECTED TACKLE",
    line: "Attrezzatura moderna selezionata da Stillwater.",
    cta: "Esplora la Selezione",
    img: "/photos/mulinelli.jpg",
    alt: "Mulinelli in controluce in riva al lago",
    testid: "shop-block-selected-tackle",
  },
  {
    to: "/shop/archive",
    title: "ARCHIVE",
    line: "Pezzi rari, fuori produzione e interessanti della storia del carpfishing.",
    cta: "Entra nell'Archivio",
    img: "/photos/carpa-notturna.jpg",
    alt: "Carpa in ricovero di notte",
    testid: "shop-block-archive",
  },
  {
    to: "/shop/editions",
    title: "STILLWATER EDITIONS",
    line: "Carta stampata, fotografia, abbigliamento e collaborazioni in edizione limitata.",
    cta: "Esplora le Edizioni",
    img: "/photos/brolly-dettaglio.jpg",
    alt: "Dettaglio del brolly in riva",
    testid: "shop-block-editions",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function ShopLanding() {
  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="shop-landing">
      <Seo
        title="Shop — Stillwater"
        description="Selected tackle, rare objects and limited editions. Una piccola collezione di attrezzatura e oggetti che usiamo, ammiriamo o riteniamo valga la pena conservare."
        canonical="https://stillwatermag.eu/shop"
      />

      <section className="relative h-[72vh] min-h-[520px] overflow-hidden flex items-end">
        <img
          src="/photos/campeggio-riva.jpg"
          alt="Bivacco in riva al lago al tramonto"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="shop-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative z-10 px-5 md:px-10 pb-12 md:pb-16">
          <motion.p
            variants={reveal}
            initial="hidden"
            animate="show"
            className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-ember mb-4"
            data-testid="shop-eyebrow"
          >
            Stillwater Shop
          </motion.p>
          <motion.h1
            variants={reveal}
            initial="hidden"
            animate="show"
            className="font-serif font-light text-3xl md:text-6xl tracking-tight leading-tight max-w-3xl"
            data-testid="shop-title"
          >
            Selected tackle, rare objects &{" "}
            <span className="italic text-ember">limited editions</span>
          </motion.h1>
          <motion.p
            variants={reveal}
            initial="hidden"
            animate="show"
            className="mt-5 max-w-xl text-sm md:text-base text-mist leading-relaxed"
          >
            "Una piccola collezione di attrezzatura e oggetti che usiamo,
            ammiriamo o riteniamo valga la pena tenere in giro."
          </motion.p>
        </div>
      </section>

      <section className="border-t border-ink-border">
        {BLOCKS.map((b, i) => (
          <motion.div
            key={b.to}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid={b.testid}
            className={`border-b border-ink-border grid grid-cols-1 md:grid-cols-2 ${
              i % 2 === 1 ? "md:[direction:rtl]" : ""
            }`}
          >
            <Link to={b.to} className="group relative block aspect-[16/9] md:aspect-[3/2] overflow-hidden">
              <img
                src={b.img}
                alt={b.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent md:bg-gradient-to-r" />
            </Link>
            <div
              className={`flex flex-col justify-center px-5 md:px-12 py-12 md:py-20 [direction:ltr] ${
                i % 2 === 1 ? "md:text-left" : ""
              }`}
            >
              <h2 className="font-mono text-xs md:text-sm uppercase tracking-[0.35em] text-ember">
                {b.title}
              </h2>
              <p className="mt-5 font-serif font-light text-2xl md:text-4xl tracking-tight leading-snug max-w-md">
                {b.line}
              </p>
              <Link
                to={b.to}
                data-testid={`${b.testid}-cta`}
                className="group/cta mt-8 inline-flex items-center gap-3 w-fit font-mono text-[11px] uppercase tracking-[0.25em] border border-bone/30 rounded-full px-6 py-3 hover:border-ember hover:text-ember transition-colors duration-300"
              >
                {b.cta}
                <ArrowUpRight
                  size={15}
                  strokeWidth={1.5}
                  className="transition-transform duration-300 group-hover/cta:rotate-45"
                />
              </Link>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24">
        <p className="max-w-xl text-sm text-mist leading-relaxed">
          Stillwater non è un negozio: è la vetrina di ciò che la redazione
          seleziona, usa e conserva. Pochi oggetti per volta, scelti come si
          scelgono le storie da raccontare.
        </p>
      </section>
    </div>
  );
}
